import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Loader2, RotateCcw, Save } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../../lib/api';
import { useAuth } from '../../../contexts/AuthContext';
import { useTenant } from '../../../contexts/TenantContext';
import { renderTemplate } from '../../../lib/waShare';
import { formatErr, inputCls } from '../billing/shared';

const Editor = ({ t, text, onChange }) => {
  const ref = useRef(null);
  const sample = useMemo(() => Object.fromEntries(t.fields.map((f) => [f.key, f.sample])), [t]);
  const insert = (key) => {
    const el = ref.current;
    const at = el ? el.selectionStart : text.length;
    const next = `${text.slice(0, at)}{${key}}${text.slice(el ? el.selectionEnd : at)}`;
    onChange(next);
    requestAnimationFrame(() => { if (el) { el.focus(); el.selectionStart = el.selectionEnd = at + key.length + 2; } });
  };
  return (
    <div className="grid gap-4 lg:grid-cols-2" data-testid={`template-editor-${t.key}`}>
      <div className="space-y-2">
        <textarea ref={ref} rows={14} value={text} onChange={(e) => onChange(e.target.value)} className={`${inputCls} font-mono text-xs leading-relaxed`} data-testid={`template-text-${t.key}`} />
        <div className="flex flex-wrap gap-1.5">
          {[...t.fields, { key: 'signature', label: 'Sign-off' }].map((f) => (
            <button key={f.key} type="button" onClick={() => insert(f.key)} title={f.label}
              className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 font-mono text-[11px] text-slate-700 hover:border-teal-400 hover:text-teal-700"
              data-testid={`template-field-${t.key}-${f.key}`}>{`{${f.key}}`}</button>
          ))}
        </div>
        <p className="text-[11px] text-slate-400">Tap a field to insert it. A line disappears when its field is empty, for example no pay link.</p>
      </div>
      <div>
        <div className="mb-1 text-xs uppercase tracking-wider text-slate-500">Preview</div>
        <pre className="whitespace-pre-wrap rounded-xl border border-emerald-100 bg-emerald-50 p-3 font-sans text-xs text-slate-800" data-testid={`template-preview-${t.key}`}>{renderTemplate(text, sample, { noReview: ['review_request', 'review_reminder', 'review_thanks'].includes(t.key) })}</pre>
      </div>
    </div>
  );
};

export const MessageTemplates = () => {
  const { authHeader } = useAuth();
  const { refresh: refreshTenant } = useTenant();
  const [list, setList] = useState(null);
  const [texts, setTexts] = useState({});
  const [active, setActive] = useState('estimate');
  const [saving, setSaving] = useState(false);

  const load = (data) => { setList(data.templates); setTexts(Object.fromEntries(data.templates.map((t) => [t.key, t.text]))); };
  useEffect(() => {
    axios.get(`${API}/billing/message-templates`, { headers: authHeader() }).then((r) => load(r.data)).catch((e) => toast.error(formatErr(e)));
  }, [authHeader]);

  if (!list) return <div className="p-6 text-center text-slate-500"><Loader2 className="inline animate-spin" /></div>;
  const t = list.find((x) => x.key === active);
  const dirty = texts[active] !== t.text;
  const groups = [...new Set(list.map((x) => x.group))];

  const save = async (value) => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/billing/message-templates`, { templates: { [active]: value } }, { headers: authHeader() });
      load(data); refreshTenant();
      toast.success(value === null ? 'Reset to the default wording' : 'Message saved');
    } catch (e) { toast.error(formatErr(e)); } finally { setSaving(false); }
  };

  return (
    <section className="space-y-4 rounded-xl border border-slate-200 bg-white p-4" data-testid="message-templates-section">
      <div>
        <h2 className="text-sm font-semibold text-slate-900">Customer messages</h2>
        <p className="mt-0.5 text-xs text-slate-500">The wording of every WhatsApp message (and the review request email). Changes apply to everyone in your workshop.</p>
      </div>
      <div className="space-y-2">
        {groups.map((g) => (
          <div key={g} className="flex flex-wrap items-center gap-1.5">
            <span className="w-36 shrink-0 text-[11px] font-semibold uppercase tracking-wider text-slate-400">{g}</span>
            {list.filter((x) => x.group === g).map((x) => (
              <button key={x.key} type="button" onClick={() => setActive(x.key)} data-testid={`template-tab-${x.key}`}
                className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${active === x.key ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-300 text-slate-600 hover:border-slate-400'}`}>
                {x.label}{x.text !== x.default && <span className="ml-1 text-teal-400" title="Customised">•</span>}
              </button>
            ))}
          </div>
        ))}
      </div>
      <Editor t={t} text={texts[active]} onChange={(v) => setTexts((s) => ({ ...s, [active]: v }))} />
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" disabled={saving || !dirty} onClick={() => save(texts[active])} data-testid="template-save-btn"
          className="inline-flex items-center gap-1.5 rounded-lg bg-teal-500 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-600 disabled:bg-teal-300">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save message
        </button>
        <button type="button" disabled={saving || t.text === t.default} onClick={() => save(null)} data-testid="template-reset-btn"
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
          <RotateCcw size={14} /> Reset to default
        </button>
        {dirty && <span className="text-xs text-amber-600" data-testid="template-unsaved">Unsaved changes</span>}
      </div>
    </section>
  );
};
